import React, { useEffect, useRef, useState } from "react";
import { fmtDate, fmtDateTime } from "@/lib/dateUtils";
import { usePager, Pager } from "@/components/ui/TablePager";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, BookOpen, Image as ImageIcon, ScrollText, PenTool, FileText,
  Gamepad2, MessageCircle, Library as LibraryIcon, X, Upload, Camera, Sparkles,
  Trash2, ChevronRight, Loader2, ClipboardList, Wand2, Send, Download, Layers,
  RotateCw, Pencil, GripVertical, FolderOpen,
} from "lucide-react";
import QuizOverlay from "@/components/quiz/QuizOverlay";
import StoryQuizPlayer from "@/components/quiz/StoryQuizPlayer";
import AnimatedStoryOverlay from "@/components/story/AnimatedStoryOverlay";
import SmartCardsBody from "@/components/cards/SmartCardsBody";
import { MillionaireBody } from "@/components/game/MillionaireGame";
import SlidesBody from "@/components/slides/SlidesBody";
import CreationsBody from "@/components/creations/CreationsBody";

const TOOLS = [
  { id: "scan", label: "Scan", icon: ImageIcon, color: "#0EA5E9", grad: "from-[#0EA5E9] to-[#38BDF8]" },
  { id: "creations", label: "My Creations", icon: FolderOpen, color: "#10B981", grad: "from-[#10B981] to-[#34D399]" },
  { id: "story", label: "Story", icon: ScrollText, color: "#F97316", grad: "from-[#F97316] to-[#FB923C]" },
  { id: "cards", label: "Smart Cards", icon: Layers, color: "#EC4899", grad: "from-[#EC4899] to-[#F472B6]" },
  { id: "workbook", label: "Workbook", icon: ClipboardList, color: "#EC4899", grad: "from-[#EC4899] to-[#F472B6]" },
  { id: "quiz", label: "AI Quiz", icon: ScrollText, color: "#7C3AED", grad: "from-[#7C3AED] to-[#A78BFA]" },
  { id: "game", label: "Game", icon: Gamepad2, color: "#A855F7", grad: "from-[#A855F7] to-[#C084FC]" },
  { id: "slides", label: "Slides", icon: FileText, color: "#0891B2", grad: "from-[#0891B2] to-[#06B6D4]" },
  { id: "lesson", label: "Lesson Plan", icon: BookOpen, color: "#0891B2", grad: "from-[#0891B2] to-[#06B6D4]" },
  { id: "whiteboard", label: "Interactive Board", icon: PenTool, color: "#6366F1", grad: "from-[#6366F1] to-[#818CF8]" },
  { id: "ask", label: "Ask Me", icon: MessageCircle, color: "#10B981", grad: "from-[#10B981] to-[#34D399]" },
  { id: "past", label: "Past", icon: FileText, color: "#F59E0B", grad: "from-[#F59E0B] to-[#FBBF24]" },
  { id: "resources", label: "Resources", icon: LibraryIcon, color: "#64748B", grad: "from-[#64748B] to-[#94A3B8]" },
];

export default function SubjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [subject, setSubject] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [activeChapter, setActiveChapter] = useState(null);
  const [activeTool, setActiveTool] = useState("scan");
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [zoomPage, setZoomPage] = useState(null);

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [id]);
  const loadAll = async () => {
    setLoading(true);
    try {
      const [subj, chs] = await Promise.all([api.get(`/subjects/${id}`), api.get(`/chapters?subject_id=${id}`)]);
      setSubject(subj.data.subject);
      const list = chs.data.chapters || [];
      setChapters(list);
      setActiveChapter(list[0] || null);
    } catch (e) { toast.error("Failed to load subject"); navigate("/teacher/subjects"); }
    finally { setLoading(false); }
  };

  const refreshChapters = async () => {
    try {
      const { data } = await api.get(`/chapters?subject_id=${id}`);
      setChapters(data.chapters || []);
      if (activeChapter) {
        const u = (data.chapters || []).find((c) => c.id === activeChapter.id);
        if (u) setActiveChapter(u);
      } else if ((data.chapters || []).length > 0) {
        setActiveChapter(data.chapters[0]);
      }
    } catch {}
  };

  const deleteChapter = async (cid) => {
    if (!window.confirm("Delete this chapter and its pages permanently?")) return;
    try {
      await api.delete(`/chapters/${cid}`);
      const next = chapters.filter((c) => c.id !== cid);
      setChapters(next);
      if (activeChapter?.id === cid) setActiveChapter(next[0] || null);
      toast.success("Chapter removed");
    } catch { toast.error("Delete failed"); }
  };

  const renameChapter = async (cid, title) => {
    try {
      await api.patch(`/chapters/${cid}`, { title });
      setChapters((cur) => cur.map((c) => (c.id === cid ? { ...c, title } : c)));
      if (activeChapter?.id === cid) setActiveChapter((c) => ({ ...c, title }));
      toast.success("Chapter renamed");
    } catch (e) { toast.error(e.response?.data?.detail || "Rename failed"); }
  };

  const reorderChapters = async (list) => {
    setChapters(list);
    try {
      await api.post("/chapters/reorder", { subject_id: id, ordered_ids: list.map((c) => c.id) });
    } catch { toast.error("Could not save the new order"); }
  };

  if (loading) return <div className="text-sm text-neutral-500 inline-flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> Loading subject…</div>;
  if (!subject) return null;

  return (
    <div className="-mx-4 lg:-mx-8 -my-6 min-h-[calc(100vh-4rem)] flex flex-col" data-testid="subject-detail-page">
      {/* Header row */}
      <div className="px-4 lg:px-8 py-5 border-b themed-border flex items-center gap-3">
        <button onClick={() => navigate("/teacher/subjects")} className="text-xs text-neutral-500 hover:text-[#0A0C10] inline-flex items-center gap-1" data-testid="back-to-subjects">
          <ArrowLeft size={12} /> All subjects
        </button>
        <span className="text-neutral-400">·</span>
        <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-neutral-500">{subject.grade_name}</div>
        <h1 className="font-display font-bold text-xl tracking-tighter ml-1">{subject.name}</h1>
      </div>

      {/* Two columns separated by a sleek vertical line */}
      <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] flex-1 min-h-0">
        {/* ── Column: Chapters ── */}
        <ChaptersColumn
          subject={subject}
          chapters={chapters}
          active={activeChapter}
          onPick={(c) => { setActiveChapter(c); setActiveTool("scan"); }}
          onAdd={() => setShowAdd(true)}
          onDelete={deleteChapter}
          onRename={renameChapter}
          onReorder={reorderChapters}
        />

        {/* ── Column: Tool strip + inline workspace ── */}
        <div className="flex flex-col min-w-0">
          <ToolStrip activeTool={activeTool} onPick={setActiveTool} chapter={activeChapter} />
          <div className="px-5 lg:px-8 py-6 flex-1 overflow-y-auto" data-testid="tool-workspace">
            {activeChapter ? (
              <ToolBody tool={activeTool} chapter={activeChapter} subject={subject} onZoomPage={setZoomPage} refreshChapters={refreshChapters} allChapters={chapters} />
            ) : activeTool === "creations" ? (
              // My Creations works without a chapter — it lists every saved AI asset for this subject.
              <CreationsBody subject={subject} chapter={null} />
            ) : activeTool === "lesson" ? (
              // Lesson Plan is available even without a chapter — it surfaces
              // subject-scoped outputs from the one-click lesson generator so
              // teachers can still browse generated slides, plans and quizzes.
              <LessonBody chapter={null} subject={subject} />
            ) : (
              <EmptyChapter onAdd={() => setShowAdd(true)} />
            )}
          </div>
        </div>
      </div>

      {showAdd && <AddChapterModal subjectId={id} onClose={() => setShowAdd(false)} onCreated={async () => { setShowAdd(false); await refreshChapters(); }} />}
      {zoomPage && <PageZoom page={zoomPage} onClose={() => setZoomPage(null)} />}
    </div>
  );
}

// ───────── Chapters column ─────────
function ChaptersColumn({ subject, chapters, active, onPick, onAdd, onDelete, onRename, onReorder }) {
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [dragId, setDragId] = useState(null);

  const saveRename = (c) => {
    const t = editTitle.trim();
    setEditingId(null);
    if (t && t !== c.title) onRename?.(c.id, t.slice(0, 120));
  };
  const handleDrop = (targetId) => {
    if (!dragId || dragId === targetId) { setDragId(null); return; }
    const list = [...chapters];
    const from = list.findIndex((c) => c.id === dragId);
    const to = list.findIndex((c) => c.id === targetId);
    if (from < 0 || to < 0) { setDragId(null); return; }
    const [moved] = list.splice(from, 1);
    list.splice(to, 0, moved);
    onReorder?.(list);
    setDragId(null);
  };

  return (
    <aside className="border-r themed-border px-4 lg:px-6 py-6 min-w-0">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-neutral-500">Chapters</div>
          <div className="font-display font-bold text-lg tracking-tighter text-[#0A0C10] mt-0.5">{subject.name}</div>
        </div>
        <button
          onClick={onAdd}
          data-testid="add-chapter-btn"
          className="w-8 h-8 rounded-md bg-[#0A0C10] text-[#FBFAF7] hover:bg-[#0A0C10]/85 transition-colors inline-flex items-center justify-center"
          title="Add chapter"
        ><Plus size={15} strokeWidth={1.8} /></button>
      </div>
      <div className="space-y-1.5 max-h-[calc(100vh-220px)] overflow-y-auto pr-1 -mr-1">
        {chapters.length === 0 && (
          <div className="text-xs text-neutral-500 py-8 text-center border border-dashed themed-border rounded-md">
            No chapters yet — tap +.
          </div>
        )}
        {chapters.map((c, idx) => {
          const isActive = active?.id === c.id;
          const preview = (c.pages?.[0]?.summary || c.description || "Open to scan pages…").slice(0, 80);
          const isEditing = editingId === c.id;
          return (
            <div
              key={c.id}
              data-testid={`chapter-card-${c.id}`}
              onClick={() => !isEditing && onPick(c)}
              draggable={!isEditing}
              onDragStart={() => setDragId(c.id)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => handleDrop(c.id)}
              onDragEnd={() => setDragId(null)}
              className={`group rounded-md p-3 cursor-pointer transition-colors duration-200 ${isActive
                ? "bg-[#0A0C10] text-[#FBFAF7]"
                : "hover:bg-black/5"} ${dragId === c.id ? "opacity-50" : ""}`}
            >
              <div className="flex items-start gap-2.5">
                <div className={`shrink-0 flex items-center gap-0.5 ${isActive ? "text-[#FBFAF7]/40" : "text-neutral-300"}`}>
                  <GripVertical size={12} className="cursor-grab opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className={`w-7 h-7 rounded-sm inline-flex items-center justify-center text-[10px] font-mono font-bold ${isActive ? "bg-[#FBFAF7]/15 text-[#FBFAF7]" : "bg-black/5 text-neutral-600"}`}>
                    {String(idx + 1).padStart(2, "0")}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  {isEditing ? (
                    <input
                      autoFocus
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") saveRename(c);
                        if (e.key === "Escape") setEditingId(null);
                      }}
                      onBlur={() => saveRename(c)}
                      data-testid={`chapter-rename-input-${c.id}`}
                      className={`w-full text-[13.5px] font-semibold bg-transparent border rounded px-1.5 py-0.5 outline-none ${isActive ? "border-[#FBFAF7]/40 text-[#FBFAF7]" : "border-neutral-300 text-[#0A0C10]"}`}
                    />
                  ) : (
                    <div className={`font-semibold text-[13.5px] leading-tight ${isActive ? "text-[#FBFAF7]" : "text-[#0A0C10]"}`}>{c.title}</div>
                  )}
                  <div className={`text-[11.5px] mt-1 leading-snug line-clamp-2 ${isActive ? "text-[#FBFAF7]/70" : "text-neutral-500"}`}>{preview}</div>
                  <div className={`text-[10px] font-mono uppercase tracking-[0.18em] mt-1.5 ${isActive ? "text-[#FBFAF7]/60" : "text-neutral-400"}`}>
                    {(c.pages || []).length} page{(c.pages || []).length === 1 ? "" : "s"}
                  </div>
                </div>
                <div className="flex flex-col items-center gap-1.5">
                  <button
                    onClick={(e) => { e.stopPropagation(); setEditingId(c.id); setEditTitle(c.title); }}
                    data-testid={`chapter-rename-${c.id}`}
                    title="Rename chapter"
                    className={`opacity-0 group-hover:opacity-100 transition-opacity ${isActive ? "text-[#FBFAF7]/70 hover:text-[#FBFAF7]" : "text-neutral-400 hover:text-[#0A0C10]"}`}
                  ><Pencil size={12} /></button>
                  <button
                    onClick={(e) => { e.stopPropagation(); onDelete(c.id); }}
                    data-testid={`chapter-del-${c.id}`}
                    className={`opacity-0 group-hover:opacity-100 transition-opacity ${isActive ? "text-[#FBFAF7]/70 hover:text-[#FBFAF7]" : "text-red-500 hover:text-red-700"}`}
                  ><Trash2 size={12} /></button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {chapters.length > 1 && (
        <div className="text-[10px] text-neutral-400 mt-2 px-1">Drag cards to reorder · pencil to rename</div>
      )}
    </aside>
  );
}

// ───────── Tool strip (flat editorial tabs) ─────────
function ToolStrip({ activeTool, onPick, chapter }) {
  return (
    <div className="px-5 lg:px-8 pt-5 pb-4 border-b themed-border">
      <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-neutral-500 mb-3">
        / chapter · <span className="text-[#0A0C10]">{chapter?.title || "—"}</span>
      </div>
      <div className="flex flex-wrap gap-1" data-testid="tool-strip">
        {TOOLS.map((t) => (
          <ToolTab key={t.id} tab={t} active={activeTool === t.id} onClick={() => onPick(t.id)} />
        ))}
      </div>
    </div>
  );
}

function ToolTab({ tab, active, onClick }) {
  const Icon = tab.icon;
  return (
    <button
      onClick={onClick}
      data-testid={`tab-${tab.id}`}
      className={`inline-flex items-center gap-2 px-3 py-2 rounded-md text-[13px] font-medium transition-colors duration-200 ${active
        ? "bg-[#0A0C10] text-[#FBFAF7]"
        : "text-neutral-600 hover:bg-black/5 hover:text-[#0A0C10]"}`}
    >
      <Icon size={15} strokeWidth={1.6} style={!active ? { color: tab.color } : undefined} />
      <span>{tab.label}</span>
    </button>
  );
}

// ───────── Inline tool bodies ─────────
function ToolBody({ tool, chapter, subject, onZoomPage, refreshChapters, allChapters }) {
  if (tool === "scan") return <ScanBody allChapters={allChapters} activeId={chapter.id} onZoomPage={onZoomPage} />;
  if (tool === "creations") return <CreationsBody subject={subject} chapter={chapter} />;
  if (tool === "ask") return <AskBody chapter={chapter} subject={subject} />;
  if (tool === "quiz") return <QuizBody chapter={chapter} subject={subject} />;
  if (tool === "lesson") return <LessonBody chapter={chapter} subject={subject} />;
  if (tool === "workbook") return <WorkbookBody chapter={chapter} subject={subject} />;
  if (tool === "story") return <StoryBody chapter={chapter} subject={subject} />;
  if (tool === "cards") return <SmartCardsBody chapter={chapter} subject={subject} />;
  if (tool === "game") return <MillionaireBody chapter={chapter} subject={subject} />;
  if (tool === "slides") return <SlidesBody chapter={chapter} subject={subject} />;
  if (tool === "whiteboard") return <WhiteboardBody chapter={chapter} subject={subject} />;
  if (tool === "resources") return <ResourcesBody subject={subject} chapter={chapter} refreshChapters={refreshChapters} />;
  if (tool === "past") return <PastBody subject={subject} chapter={chapter} />;
  return null;
}

function ScanBody({ allChapters, activeId, onZoomPage }) {
  // Flatten and group: every chapter contributes its pages.
  const groups = (allChapters || []).map((c) => ({
    id: c.id, title: c.title, pages: c.pages || [], isActive: c.id === activeId,
  })).filter((g) => g.pages.length > 0);
  const totalPages = groups.reduce((acc, g) => acc + g.pages.length, 0);
  return (
    <div data-testid="scan-body">
      <Heading
        label="Scan"
        color="#0EA5E9"
        title="All scanned book pages 📖"
        sub={`${totalPages} page${totalPages === 1 ? "" : "s"} across ${groups.length} chapter${groups.length === 1 ? "" : "s"} · tap any to zoom`}
      />
      {totalPages === 0 ? (
        <EmptyBody msg="No pages scanned yet — use the + on the Chapters list to scan or upload." icon={ImageIcon} />
      ) : (
        <div className="mt-5 space-y-8">
          {groups.map((g) => (
            <div key={g.id}>
              <div className="flex items-center gap-2 mb-3">
                <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-[#0EA5E9] font-bold">{g.title}</div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-neutral-400">· {g.pages.length} page{g.pages.length === 1 ? "" : "s"}</div>
                {g.isActive && <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 bg-[#0A0C10] text-[#FBFAF7] rounded-sm">current</span>}
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {g.pages.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => onZoomPage(p)}
                    data-testid={`page-thumb-${p.id}`}
                    className="group relative rounded-md overflow-hidden bg-[#FBFAF7] aspect-[3/4] border border-neutral-100 hover:border-[#0EA5E9] transition-all duration-200 shadow-sm hover:shadow-md"
                  >
                    <img src={pageUrl(p.image_url)} alt={p.summary} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute top-2 left-2 px-2 py-0.5 bg-[#0EA5E9] text-white text-[11px] font-mono font-bold rounded-sm">P{p.order}</div>
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2 text-left">
                      <div className="text-white text-[11px] font-medium leading-tight line-clamp-2">{p.summary}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AskBody({ chapter, subject }) {
  const [msg, setMsg] = useState("");
  const [history, setHistory] = useState([{ role: "assistant", text: `Ask me anything about ${chapter.title}. I've read all the pages.` }]);
  const [busy, setBusy] = useState(false);
  const send = async (e) => {
    e?.preventDefault();
    const t = msg.trim();
    if (!t || busy) return;
    const next = [...history, { role: "user", text: t }];
    setHistory(next); setMsg(""); setBusy(true);
    try {
      const context = (chapter.pages || []).slice(0, 6).map((p) => `Page ${p.order}: ${p.ocr_text || p.summary}`).join("\n\n").slice(0, 12000);
      const { data } = await api.post("/ai/chat", {
        persona: "tutor",
        message: `Chapter: ${chapter.title}\nSubject: ${subject.name}\nContext from book pages:\n${context}\n\nQuestion: ${t}`,
        history: next.slice(-6).map((m) => ({ role: m.role, content: m.text })),
      });
      setHistory((cur) => [...cur, { role: "assistant", text: data?.reply || "(no reply)" }]);
    } catch { toast.error("Ask failed"); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="ask-body">
      <Heading label="Ask Me" color="#10B981" title="Chat with this chapter" sub="Grounded in the OCR'd pages." />
      <div className="mt-4 space-y-2 max-h-80 overflow-y-auto">
        {history.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm ${m.role === "user" ? "bg-[#10B981] text-white" : "bg-[#FBFAF7] text-[#0A0C10] border border-neutral-100"}`}>{m.text}</div>
          </div>
        ))}
        {busy && <div className="text-xs text-neutral-500 inline-flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> Thinking…</div>}
      </div>
      <form onSubmit={send} className="mt-4 flex gap-2">
        <input value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Ask anything about this chapter…" data-testid="ask-input" className="flex-1 px-4 py-2.5 rounded-md border themed-border focus:border-[#0A0C10] outline-none text-sm bg-white" />
        <button type="submit" disabled={busy} data-testid="ask-send" className="px-5 py-2.5 rounded-md bg-[#0A0C10] text-[#FBFAF7] text-sm font-medium disabled:opacity-50 inline-flex items-center gap-1.5 hover:bg-[#0A0C10]/90 transition-colors"><Send size={14} /> Send</button>
      </form>
    </div>
  );
}

// Generic generator pattern — used by quiz, lesson, workbook, story, game
function GeneratorBody({ tool, chapter, subject, label, color, helperText, ctaLabel, run, renderResult }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const generate = async () => {
    setBusy(true); setResult(null);
    try {
      const out = await run();
      setResult(out);
      toast.success(`${label} ready`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid={`${tool}-body`}>
      <Heading label={label} color={color} title={`Build ${label.toLowerCase()} for "${chapter.title}"`} sub={helperText} />
      <button
        onClick={generate}
        disabled={busy || (chapter.pages || []).length === 0}
        data-testid={`${tool}-generate`}
        className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-[#0A0C10] text-[#FBFAF7] text-sm font-medium hover:bg-[#0A0C10]/90 transition-colors disabled:opacity-50"
      >
        {busy ? <><Loader2 size={14} className="animate-spin" /> Generating…</> : <><Wand2 size={14} style={{ color }} /> {ctaLabel}</>}
      </button>
      {(chapter.pages || []).length === 0 && (
        <div className="text-[11px] text-neutral-500 mt-2">Scan some pages first so the AI has something to learn from.</div>
      )}
      {result && <div className="mt-5">{renderResult(result)}</div>}
    </div>
  );
}

function QuizBody({ chapter, subject }) {
  const [open, setOpen] = useState(false);
  const [replay, setReplay] = useState(null);
  const [storyPlay, setStoryPlay] = useState(null);
  return (
    <div data-testid="quiz-body">
      <Heading
        label="AI Quiz" color="#7C3AED"
        title="Interactive quiz on this chapter"
        sub="Pick a style — MCQ · Drag & Drop · Fill in the blank — and play in full-screen."
      />
      <button
        onClick={() => setOpen(true)}
        disabled={(chapter.pages || []).length === 0}
        data-testid="quiz-launch"
        className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-[#7C3AED] text-white text-sm font-medium hover:bg-[#6D28D9] transition-colors disabled:opacity-50"
      >
        <Sparkles size={14} /> Launch interactive quiz
      </button>
      {(chapter.pages || []).length === 0 && (
        <div className="text-[11px] text-neutral-500 mt-2">Scan some pages first so the AI has something to learn from.</div>
      )}
      <div className="mt-6 grid sm:grid-cols-3 gap-3 max-w-3xl">
        <PreviewCard color="#7C3AED" label="MCQ" desc="4 options, pick one, big-bold question with timer." />
        <PreviewCard color="#0EA5E9" label="Drag & Drop" desc="Drag answer tiles into the right zones." />
        <PreviewCard color="#10B981" label="Fill in the Blank" desc="Bold sentence with animated input boxes." />
      </div>
      <SavedQuizzesSection subject={subject} chapter={chapter} onPlay={setReplay} onPlayStory={setStoryPlay} refreshKey={open} />
      {storyPlay && (
        <StoryQuizPlayer open onClose={() => setStoryPlay(null)} quiz={storyPlay} timePerQ={60} />
      )}
      <QuizOverlay
        open={open || !!replay}
        onClose={() => { setOpen(false); setReplay(null); }}
        chapter={chapter}
        subject={subject}
        savedQuiz={replay}
      />
      <QuizResultsSection subject={subject} />
    </div>
  );
}

function SavedQuizzesSection({ subject, chapter, onPlay, onPlayStory, refreshKey }) {
  const [quizzes, setQuizzes] = useState(null);
  const load = async () => {
    try {
      const { data } = await api.get("/ai/quiz", { params: { subject_id: subject.id } });
      setQuizzes(data.quizzes || []);
    } catch { setQuizzes([]); }
  };
  useEffect(() => { if (!refreshKey) load(); /* eslint-disable-next-line */ }, [subject?.id, refreshKey]);

  const del = async (q) => {
    if (!window.confirm(`Delete "${q.title}"?`)) return;
    try {
      await api.delete(`/ai/quiz/${q.id}`);
      setQuizzes((cur) => cur.filter((x) => x.id !== q.id));
      toast.success("Quiz deleted");
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const play = (q) => {
    const qs = q.questions || [];
    if (!qs.length) return toast.error("This quiz has no questions");
    const interactiveMcq = q.category === "mcq" && qs.some((x) => typeof x.correct_index === "number");
    const interactiveDrag = q.category === "drag_drop" && qs.some((x) => Array.isArray(x.items));
    const interactiveFill = q.category === "fill_blank" && qs.some((x) => typeof x.sentence === "string");
    if (interactiveMcq || interactiveDrag || interactiveFill) {
      onPlay(q);
    } else {
      // Standard-format quiz (Quiz Maker / live class) → story-mode player
      onPlayStory(q);
    }
  };

  if (!quizzes || quizzes.length === 0) return null;
  return (
    <div className="mt-8 max-w-3xl" data-testid="saved-quizzes-section">
      <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-neutral-500 mb-3">/ saved quizzes · always yours</div>
      <div className="space-y-2">
        {quizzes.slice(0, 25).map((q) => {
          const isChapter = q.chapter_id && q.chapter_id === chapter?.id;
          return (
            <div key={q.id} className="flex items-center gap-3 bg-white border border-black/8 rounded-lg px-4 py-3" data-testid={`saved-quiz-${q.id}`}>
              <div className="w-8 h-8 rounded-md bg-[#7C3AED]/10 text-[#7C3AED] inline-flex items-center justify-center shrink-0">
                <Sparkles size={13} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{q.title}</div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-neutral-400">
                  {(q.category || "mcq").replace("_", " ")} · {(q.questions || []).length} Qs · {fmtDate(q.created_at)}
                  {isChapter && <span className="text-[#7C3AED]"> · this chapter</span>}
                </div>
              </div>
              <button onClick={() => play(q)} data-testid={`saved-quiz-play-${q.id}`}
                className="text-xs font-bold px-3 py-1.5 rounded-md bg-[#7C3AED] text-white hover:bg-[#6D28D9]">Play</button>
              <button onClick={() => del(q)} data-testid={`saved-quiz-del-${q.id}`}
                className="p-1.5 text-neutral-400 hover:text-red-500 rounded hover:bg-red-50"><Trash2 size={13} /></button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function QuizResultsSection({ subject }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/live/quiz-results/mine", { params: subject?.id ? { subject_id: subject.id } : {} });
        setRows((data.results || []).sort((a, b) => (b.submitted_at || "").localeCompare(a.submitted_at || "")));
      } catch { setRows([]); }
    })();
  }, [subject?.id]);
  const pager = usePager(rows || [], 25);
  if (rows === null) return <div className="mt-8 text-xs text-neutral-500 inline-flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Loading quiz results…</div>;
  return (
    <div className="mt-8" data-testid="quiz-results-section">
      <Heading label="Quiz results" color="#10B981" title="Student results from your live classes" sub="Every answer submitted during a shared quiz lands here automatically." />
      {rows.length === 0 ? (
        <div className="mt-4 text-xs text-neutral-500 border themed-border rounded-md p-4 bg-[#FBFAF7]" data-testid="quiz-results-empty">
          No results yet — share a quiz in a live class and student scores will appear here.
        </div>
      ) : (
        <div className="mt-4 border themed-border rounded-md overflow-hidden overflow-x-auto bg-white">
          <table className="w-full text-sm" data-testid="quiz-results-table">
            <thead className="bg-[#FBFAF7] text-[10px] font-mono uppercase tracking-[0.18em] text-neutral-500">
              <tr>
                <th className="px-3 py-2.5 text-left">Student</th>
                <th className="px-3 py-2.5 text-left">Quiz</th>
                <th className="px-3 py-2.5 text-left">Class</th>
                <th className="px-3 py-2.5 text-right">Score</th>
                <th className="px-3 py-2.5 text-right">Time</th>
                <th className="px-3 py-2.5 text-right">Status</th>
                <th className="px-3 py-2.5 text-right">When</th>
              </tr>
            </thead>
            <tbody>
              {pager.pageItems.map((r, i) => (
                <tr key={`${r.student_id}-${r.quiz_id}-${i}`} className="border-t border-neutral-100">
                  <td className="px-3 py-2 font-semibold">{r.student_name}</td>
                  <td className="px-3 py-2 text-neutral-600">{r.quiz_title}</td>
                  <td className="px-3 py-2 text-neutral-500 text-xs">{r.class_title}</td>
                  <td className="px-3 py-2 text-right font-mono font-bold" style={{ color: r.score / Math.max(1, r.total) >= 0.6 ? "#10B981" : "#F59E0B" }}>
                    {r.score}/{r.total}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-xs">{r.time_s}s</td>
                  <td className="px-3 py-2 text-right"><span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded ${r.status === "final" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{r.status}</span></td>
                  <td className="px-3 py-2 text-right text-[11px] font-mono text-neutral-500">{fmtDateTime(r.submitted_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pager page={pager.page} pages={pager.pages} total={pager.total} onPage={pager.setPage} testid="quiz-results-pager" />
        </div>
      )}
    </div>
  );
}
function PreviewCard({ color, label, desc }) {
  return (
    <div className="bg-[#FBFAF7] border themed-border rounded-md p-4">
      <div className="text-[11px] font-mono uppercase tracking-[0.22em] font-bold" style={{ color }}>{label}</div>
      <p className="text-xs text-neutral-600 mt-1.5 leading-relaxed">{desc}</p>
    </div>
  );
}

function LessonBody({ chapter, subject }) {
  const [bundleOut, setBundleOut] = useState(null);
  const [loadingBundle, setLoadingBundle] = useState(false);

  const loadBundleOutputs = React.useCallback(async () => {
    if (!subject?.id) return;
    setLoadingBundle(true);
    try {
      const { data } = await api.get(`/ai/lesson-bundle/by-subject/${subject.id}`);
      setBundleOut(data);
    } catch { /* ignore */ } finally { setLoadingBundle(false); }
  }, [subject?.id]);

  useEffect(() => { loadBundleOutputs(); }, [loadBundleOutputs]);

  const total = bundleOut ? (
    (bundleOut.slide_decks || []).length +
    (bundleOut.lesson_plans || []).length +
    (bundleOut.quizzes || []).length +
    (bundleOut.homework || []).length +
    (bundleOut.smart_card_decks || []).length
  ) : 0;

  return (
    <div className="space-y-4">
      {/* Subject-scoped bundle outputs (one-click lesson generation) */}
      {(total > 0 || loadingBundle) && (
        <div className="bg-white border border-black/10 rounded-xl overflow-hidden" data-testid="lesson-plan-bundle-outputs">
          <div className="px-4 py-3 border-b border-black/5 bg-[#F5FEFF] flex items-center justify-between">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#0891B2]">/ one-click lesson outputs</div>
              <div className="font-bold text-sm mt-0.5">Generated for {subject?.name || "this subject"}</div>
            </div>
            <span className="text-[10px] font-mono uppercase tracking-wider text-neutral-500">{total} items</span>
          </div>
          {loadingBundle ? (
            <div className="p-4 text-xs text-neutral-500">Loading…</div>
          ) : (
            <div className="divide-y divide-black/5">
              {(bundleOut?.slide_decks || []).slice(0, 12).map((d) => (
                <BundleRow key={`s-${d.id}`} kind="Slides" tint="#7C3AED" title={d.title} sub={`${(d.slides || []).length} slides · ${d.theme || "default"}`} createdAt={d.created_at}
                  href={`/teacher/subjects/${subject.id}/slides/${d.id}`}
                  testid={`bundle-slide-${d.id}`} />
              ))}
              {(bundleOut?.lesson_plans || []).slice(0, 8).map((p) => (
                <BundleRow key={`p-${p.id}`} kind="Lesson Plan" tint="#0891B2" title={p.syllabus?.slice(0, 80) || `Plan · ${p.subject}`} sub={p.grade || "—"} createdAt={p.created_at}
                  testid={`bundle-plan-${p.id}`} />
              ))}
              {(bundleOut?.quizzes || []).slice(0, 8).map((q) => (
                <BundleRow key={`q-${q.id}`} kind={q.kind === "exit_ticket" ? "Exit Ticket" : "Quiz"} tint="#F59E0B"
                  title={q.title} sub={`${(q.questions || []).length} Q · ${q.duration_minutes || 15} min`} createdAt={q.created_at}
                  testid={`bundle-quiz-${q.id}`} />
              ))}
              {(bundleOut?.homework || []).slice(0, 8).map((h) => (
                <BundleRow key={`h-${h.id}`} kind={h.kind === "assignment" ? "Assignment" : h.kind === "worksheet" ? "Worksheet" : "Homework"} tint="#EC4899"
                  title={h.title} sub={`${h.difficulty || "medium"} · ${h.topic || ""}`} createdAt={h.created_at}
                  testid={`bundle-hw-${h.id}`} />
              ))}
              {(bundleOut?.smart_card_decks || []).slice(0, 6).map((c) => (
                <BundleRow key={`c-${c.id}`} kind="Smart Cards" tint="#10B981"
                  title={c.title} sub={`${(c.cards || []).length} cards · ${c.topic || ""}`} createdAt={c.created_at}
                  testid={`bundle-cards-${c.id}`} />
              ))}
            </div>
          )}
        </div>
      )}

      {chapter && (
        <GeneratorBody
          tool="lesson" chapter={chapter} subject={subject} label="Chapter lesson plan" color="#0891B2"
          helperText="A 4-class plan covering this chapter's pages, with goals and weak topics."
          ctaLabel="Build lesson plan"
          run={async () => {
            const syllabus = (chapter.pages || []).map((p) => p.summary).join(" · ").slice(0, 1200) || chapter.title;
            const { data } = await api.post("/ai/lesson-plan", {
              subject: subject.name, grade: subject.grade_name || "—", syllabus, num_classes: 4,
              learning_goals: `Master ${chapter.title}`, subject_id: subject.id,
            });
            loadBundleOutputs();
            return data.lesson_plan;
          }}
          renderResult={(lp) => (
            <div className="bg-[#FBFAF7] rounded-xl p-4 whitespace-pre-wrap text-sm text-[#0A0C10] max-h-[400px] overflow-y-auto">{lp.plan_content}</div>
          )}
        />
      )}
    </div>
  );
}

function BundleRow({ kind, tint, title, sub, createdAt, href, testid }) {
  const dt = createdAt ? new Date(createdAt) : null;
  const label = dt ? dt.toLocaleDateString([], { day: "2-digit", month: "short" }) : "";
  const body = (
    <div className="px-4 py-2.5 flex items-center gap-3 hover:bg-neutral-50 transition-colors">
      <span className="inline-flex items-center justify-center text-[9px] font-bold tracking-wider uppercase px-1.5 py-0.5 rounded" style={{ background: tint + "20", color: tint }}>
        {kind}
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-neutral-900 truncate">{title || "Untitled"}</div>
        {sub && <div className="text-[10px] text-neutral-500 mt-0.5 truncate">{sub}</div>}
      </div>
      <span className="text-[10px] font-mono text-neutral-400 shrink-0">{label}</span>
    </div>
  );
  return href ? <a href={href} data-testid={testid} className="block">{body}</a> : <div data-testid={testid}>{body}</div>;
}

function WorkbookBody({ chapter, subject }) {
  return (
    <GeneratorBody
      tool="workbook" chapter={chapter} subject={subject} label="Workbook" color="#EC4899"
      helperText="A printable homework set with rubric, marking scheme, est. minutes."
      ctaLabel="Generate workbook"
      run={async () => {
        const objective = (chapter.pages || []).slice(0, 2).map((p) => p.summary).join(" · ").slice(0, 400) || chapter.title;
        const { data } = await api.post("/ai/homework/generate", {
          topic: chapter.title, grade: subject.grade_name || "", difficulty: "medium",
          objective, custom: "", count: 8, subject_id: subject.id,
        });
        return data.homework;
      }}
      renderResult={(hw) => {
        const qs = hw?.data?.questions || [];
        return (
          <div className="bg-white border border-black/10 rounded-xl overflow-hidden max-h-[520px] overflow-y-auto" data-testid="workbook-render">
            {/* Header ribbon */}
            <div className="bg-[#EC4899] text-white px-4 py-3 flex items-center justify-between">
              <div className="min-w-0">
                <div className="text-[10px] font-mono uppercase tracking-[0.18em] opacity-80">/ workbook</div>
                <div className="font-bold text-base truncate">{hw?.title || chapter.title}</div>
              </div>
              <div className="text-[10px] font-mono uppercase tracking-wider bg-white/20 px-2 py-1 rounded">
                {qs.length} Q · {hw?.data?.estimated_minutes || 20} min
              </div>
            </div>
            {/* Instructions */}
            {hw?.data?.instructions && (
              <div className="px-4 py-2 border-b border-black/5 bg-[#FDF2F8] text-xs text-neutral-700 italic">
                {hw.data.instructions}
              </div>
            )}
            {/* Questions */}
            <ol className="divide-y divide-black/5">
              {qs.map((q, i) => {
                const qType = (q.type || "short").toLowerCase();
                const svg = q.visual_svg || "";
                return (
                  <li key={i} className="px-4 py-3 flex gap-3">
                    <div className="text-[10px] font-mono font-bold text-[#EC4899] shrink-0 pt-0.5 w-6">Q{i + 1}</div>
                    <div className="flex-1 min-w-0">
                      {/* Visual (Ninis #23 — workbooks must be visual, not walls of text) */}
                      {svg && (
                        <div
                          className="mb-2 bg-[#FDF2F8] border border-[#EC4899]/20 rounded-lg p-2 flex items-center justify-center max-h-[140px] overflow-hidden"
                          data-testid={`wb-q${i}-visual`}
                          dangerouslySetInnerHTML={{ __html: svg }}
                        />
                      )}
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <div className="text-sm text-neutral-900 leading-snug">{q.q || q.question}</div>
                        <span className="text-[9px] font-mono uppercase tracking-wider bg-neutral-100 text-neutral-600 px-1.5 py-0.5 rounded shrink-0">
                          {qType} · {q.marks || 1}m
                        </span>
                      </div>
                      {/* MCQ options */}
                      {qType === "mcq" && Array.isArray(q.options) && (
                        <ul className="mt-1.5 space-y-1">
                          {q.options.map((opt, k) => (
                            <li key={k} className="flex items-start gap-2 text-xs text-neutral-700">
                              <span className="inline-flex items-center justify-center w-4 h-4 rounded-full border border-neutral-300 text-[10px] font-bold shrink-0 mt-0.5">
                                {String.fromCharCode(65 + k)}
                              </span>
                              <span>{opt}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                      {/* Fill-in-blank / short-answer — printable blank space */}
                      {qType !== "mcq" && (
                        <div className="mt-2 pt-2 space-y-1">
                          {Array.from({ length: qType === "short" ? 2 : 4 }).map((_, k) => (
                            <div key={k} className="border-b border-dashed border-neutral-300 h-4" />
                          ))}
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
            {/* Answer key (collapsed) */}
            {qs.some((q) => q.answer) && (
              <details className="border-t border-black/5 bg-neutral-50">
                <summary className="cursor-pointer px-4 py-2 text-xs font-semibold text-neutral-600 uppercase tracking-wider">
                  Answer key ({qs.filter((q) => q.answer).length})
                </summary>
                <ol className="px-4 pb-3 space-y-1 text-xs">
                  {qs.map((q, i) => q.answer ? (
                    <li key={i} className="flex gap-2">
                      <span className="font-mono text-[#EC4899] font-bold">Q{i + 1}:</span>
                      <span className="text-neutral-700">{q.answer}</span>
                    </li>
                  ) : null)}
                </ol>
              </details>
            )}
          </div>
        );
      }}
    />
  );
}

function StoryBody({ chapter, subject }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [story, setStory] = useState(null);
  const [saved, setSaved] = useState([]);
  const [showPromptModal, setShowPromptModal] = useState(false);
  const [customPrompt, setCustomPrompt] = useState("");

  // Load saved stories for this chapter (text-only list — no heavy image bytes)
  const reloadSaved = async () => {
    try {
      const { data } = await api.get("/ai/story", { params: { chapter_id: chapter.id } });
      setSaved(data.stories || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { reloadSaved(); /* eslint-disable-next-line */ }, [chapter.id]);

  const generate = async (prompt = "") => {
    setShowPromptModal(false);
    setOpen(true); setLoading(true); setStory(null);
    try {
      const { data } = await api.post("/ai/story/generate", {
        subject: subject.name, topic: chapter.title, grade: subject.grade_name || "",
        subject_id: subject.id, chapter_id: chapter.id,
        pages: (chapter.pages || []).map((p) => ({ order: p.order, ocr_text: p.ocr_text, summary: p.summary })),
        with_images: false,
        custom_prompt: prompt,
      });
      setStory(data.story);
      reloadSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Story generation failed");
      setOpen(false);
    } finally { setLoading(false); }
  };

  const replay = async (storyId) => {
    setOpen(true); setLoading(true); setStory(null);
    try {
      const { data } = await api.get(`/ai/story/${storyId}`);
      setStory(data.story);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load story");
      setOpen(false);
    } finally { setLoading(false); }
  };

  const remove = async (storyId) => {
    if (!confirm("Delete this story permanently?")) return;
    try { await api.delete(`/ai/story/${storyId}`); reloadSaved(); toast.success("Deleted"); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const hasPages = (chapter.pages || []).length > 0;

  return (
    <div data-testid="story-body">
      <Heading
        label="Story" color="#F97316"
        title="Animated storybook from this chapter"
        sub="Every page becomes 4 illustrated slides · saved on demand · re-open without spending credits."
      />

      {/* Actions */}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <button
          onClick={() => generate("")}
          disabled={!hasPages || loading}
          data-testid="story-launch"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-gradient-to-r from-[#FF8A4C] to-[#FF5C8A] text-white text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          {loading ? <><Loader2 size={14} className="animate-spin" /> Weaving…</> : <><Sparkles size={14} /> Generate new story</>}
        </button>
        <button
          onClick={() => setShowPromptModal(true)}
          disabled={!hasPages || loading}
          data-testid="story-custom-prompt-btn"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-white border themed-border text-sm font-medium text-[#7C2D12] hover:bg-[#FFF4ED] transition-colors disabled:opacity-50"
        >
          <Wand2 size={14} /> With custom prompt
        </button>
      </div>
      {!hasPages && <div className="text-[11px] text-neutral-500 mt-2">Scan a page first — the storybook draws from your book pages.</div>}

      {/* Saved stories library */}
      {saved.length > 0 && (
        <div className="mt-7">
          <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-neutral-500 mb-3">
            Saved stories · {saved.length}
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {saved.map((s) => (
              <SavedStoryCard key={s.id} s={s} onPlay={() => replay(s.id)} onDelete={() => remove(s.id)} />
            ))}
          </div>
        </div>
      )}

      <AnimatedStoryOverlay
        open={open}
        loading={loading}
        story={story}
        onClose={() => setOpen(false)}
      />

      {showPromptModal && (
        <CustomPromptModal
          value={customPrompt}
          onChange={setCustomPrompt}
          onClose={() => setShowPromptModal(false)}
          onSubmit={() => generate(customPrompt)}
        />
      )}
    </div>
  );
}

function SavedStoryCard({ s, onPlay, onDelete }) {
  return (
    <div className="bg-gradient-to-br from-[#FFF4ED] to-[#FFE8D5] border border-[#FF8A4C]/20 rounded-md p-3 group">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-mono uppercase tracking-wider text-[#FF5C8A] font-bold">{s.slide_count} slides · {s.image_count} images</div>
          <div className="font-display font-bold text-sm tracking-tighter text-[#7C2D12] mt-1 line-clamp-2 leading-snug">{s.title}</div>
          {s.custom_prompt && (
            <div className="text-[10px] text-[#9A3412]/70 mt-1.5 italic line-clamp-1">"{s.custom_prompt}"</div>
          )}
        </div>
        <button onClick={onDelete} data-testid={`story-del-${s.id}`} className="text-[#9A3412]/40 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={12} /></button>
      </div>
      <button onClick={onPlay} data-testid={`story-play-${s.id}`} className="mt-3 w-full inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md bg-[#0A0C10] text-white text-xs font-medium hover:bg-[#0A0C10]/90 transition-colors">
        <Sparkles size={11} /> Play
      </button>
    </div>
  );
}

function CustomPromptModal({ value, onChange, onClose, onSubmit }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-lg rounded-lg border themed-border p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="custom-prompt-modal">
        <div className="flex items-start justify-between pb-4 border-b themed-border mb-4">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-neutral-500">/ story · custom direction</div>
            <h3 className="font-display font-bold text-2xl tracking-tighter mt-1">Tell the AI what you want</h3>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-[#0A0C10] transition-colors"><X size={18} /></button>
        </div>
        <p className="text-xs text-neutral-500 mb-2">Optional — any focus, tone or audience instruction. Examples:</p>
        <div className="flex flex-wrap gap-1.5 mb-3">
          {["Use simpler vocabulary for grade 6", "Focus on trade routes & geography", "Add humour & a friendly mascot", "Tell it like a detective story"].map((e) => (
            <button key={e} onClick={() => onChange(e)} className="text-[10px] px-2 py-1 rounded-sm bg-black/5 hover:bg-black/10 text-neutral-700 transition-colors">{e}</button>
          ))}
        </div>
        <textarea
          rows={4}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="e.g. Focus on the rivalry between Florence and Venice, keep tone playful, finish with a riddle."
          data-testid="story-prompt-input"
          className="w-full px-3 py-2.5 rounded-md border themed-border text-sm outline-none focus:border-[#0A0C10] bg-white"
        />
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-4 py-2 rounded-md text-sm text-neutral-600 hover:bg-black/5 transition-colors">Cancel</button>
          <button onClick={onSubmit} data-testid="story-prompt-submit" className="px-5 py-2 rounded-md bg-[#0A0C10] text-[#FBFAF7] text-sm font-medium hover:bg-[#0A0C10]/90 transition-colors inline-flex items-center gap-2">
            <Sparkles size={13} /> Generate
          </button>
        </div>
      </div>
    </div>
  );
}

function GameBody({ chapter, subject }) {
  return (
    <GeneratorBody
      tool="game" chapter={chapter} subject={subject} label="Game" color="#A855F7"
      helperText="Flashcards / match / MCQ arcade — instant fun revision."
      ctaLabel="Generate game"
      run={async () => {
        const { data } = await api.post("/ai/game/generate", {
          subject: subject.name, topic: chapter.title, grade: subject.grade_name || "",
          kind: "match", count: 8, difficulty: "medium", subject_id: subject.id,
        });
        return data.game;
      }}
      renderResult={(g) => (
        <div className="bg-[#FBFAF7] rounded-xl p-4 text-sm">
          <div className="font-semibold mb-2">{g.title}</div>
          <pre className="text-xs whitespace-pre-wrap max-h-[300px] overflow-y-auto">{JSON.stringify(g.data, null, 2).slice(0, 3000)}</pre>
        </div>
      )}
    />
  );
}

function WhiteboardBody({ chapter, subject }) {
  const url = `/teacher/whiteboard?subjectId=${subject.id}&subject=${encodeURIComponent(subject.name)}&grade=${encodeURIComponent(subject.grade_name || "")}`;
  return (
    <div data-testid="whiteboard-body">
      <Heading label="Interactive Board" color="#6366F1" title="AI-powered interactive board for this subject" sub="Prompt → diagram. Open in a side tab to keep this chapter in view." />
      <a href={url} target="_blank" rel="noreferrer" data-testid="open-whiteboard-link" className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-[#0A0C10] text-[#FBFAF7] text-sm font-medium hover:bg-[#0A0C10]/90 transition-colors">
        <PenTool size={14} /> Open Interactive Board <ChevronRight size={14} />
      </a>
    </div>
  );
}

function ResourcesBody({ subject }) {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);
  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/library?subject_id=${subject.id}`); setAssets(data.assets || []); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", f); fd.append("name", f.name); fd.append("kind", "doc"); fd.append("subject_id", subject.id);
    try { await api.post("/library/upload", fd); toast.success("Uploaded"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };
  const BACKEND = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  return (
    <div data-testid="resources-body">
      <div className="flex items-start justify-between gap-3">
        <Heading label="Resources" color="#64748B" title="Library for this subject" sub="PDFs · slides · images" inline />
        <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="resource-upload" className="px-4 py-2 rounded-md bg-[#0A0C10] text-[#FBFAF7] text-xs font-medium inline-flex items-center gap-1.5 hover:bg-[#0A0C10]/90 transition-colors disabled:opacity-50">
          <Upload size={12} /> {uploading ? "Uploading…" : "Upload"}
        </button>
        <input ref={fileRef} type="file" hidden onChange={upload} />
      </div>
      {loading ? <div className="text-xs text-neutral-500 mt-4">Loading…</div> : assets.length === 0 ? (
        <EmptyBody msg="No resources yet — upload a PDF or image." icon={LibraryIcon} />
      ) : (
        <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {assets.map((a) => (
            <a key={a.id} href={`${BACKEND}/api${a.url}`} target="_blank" rel="noreferrer" className="bg-[#FBFAF7] rounded-xl p-3 hover:bg-white hover:shadow-md transition-all flex items-center gap-2">
              <FileText size={16} className="text-[#64748B]" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate">{a.name}</div>
                <div className="text-[10px] font-mono uppercase text-neutral-500">{a.kind} · {a.size_mb} MB</div>
              </div>
              <Download size={12} className="text-neutral-400" />
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

function PastBody({ subject }) {
  const [assets, setAssets] = useState([]);
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);
  const load = async () => {
    try { const { data } = await api.get(`/library?subject_id=${subject.id}&kind=pdf`); setAssets(data.assets || []); } catch {}
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", f); fd.append("name", f.name); fd.append("kind", "pdf"); fd.append("tags", "past-paper"); fd.append("subject_id", subject.id);
    try { await api.post("/library/upload", fd); toast.success("Past paper uploaded"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };
  const BACKEND = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  return (
    <div data-testid="past-body">
      <div className="flex items-start justify-between gap-3">
        <Heading label="Past papers" color="#F59E0B" title="Past papers archive" sub="PDFs tagged as past-paper" inline />
        <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="past-upload" className="px-4 py-2 rounded-md bg-[#0A0C10] text-[#FBFAF7] text-xs font-medium inline-flex items-center gap-1.5 hover:bg-[#0A0C10]/90 transition-colors disabled:opacity-50">
          <Upload size={12} /> {uploading ? "Uploading…" : "Upload PDF"}
        </button>
        <input ref={fileRef} type="file" accept="application/pdf" hidden onChange={upload} />
      </div>
      {assets.length === 0 ? <EmptyBody msg="No past papers yet — upload a PDF." icon={FileText} /> : (
        <div className="mt-4 grid sm:grid-cols-2 gap-2">
          {assets.map((a) => (
            <a key={a.id} href={`${BACKEND}/api${a.url}`} target="_blank" rel="noreferrer" className="bg-gradient-to-br from-[#FFF4ED] to-[#FFE4D4] rounded-xl p-3 hover:shadow-md transition-all flex items-center gap-2">
              <FileText size={16} className="text-[#F59E0B]" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm text-[#7C2D12] truncate">{a.name}</div>
                <div className="text-[10px] font-mono uppercase text-[#9A3412]/70">{a.size_mb} MB</div>
              </div>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

// ───────── helpers ─────────
function Heading({ label, color, title, sub, inline = false }) {
  return (
    <div className={inline ? "" : "mb-1"}>
      <div className="text-[11px] font-mono uppercase tracking-[0.22em] font-bold" style={{ color }}>{label}</div>
      <div className="font-display font-bold text-xl tracking-tighter">{title}</div>
      {sub && <p className="text-sm text-neutral-500 mt-1">{sub}</p>}
    </div>
  );
}
function EmptyBody({ msg, icon: Icon }) {
  return (
    <div className="mt-4 border-2 border-dashed border-neutral-200 rounded-xl p-10 text-center">
      <Icon className="mx-auto text-neutral-300" size={28} />
      <p className="mt-3 text-sm text-neutral-500">{msg}</p>
    </div>
  );
}
function EmptyChapter({ onAdd }) {
  return (
    <div className="py-16 text-center">
      <div className="w-14 h-14 mx-auto rounded-md bg-[#0A0C10] flex items-center justify-center"><BookOpen size={24} className="text-[#FBFAF7]" /></div>
      <div className="mt-4 font-display font-bold text-2xl tracking-tighter">Add your first chapter</div>
      <p className="text-sm text-neutral-500 mt-2 max-w-md mx-auto">Snap your book with the webcam or upload images — Mobi reads every page.</p>
      <button onClick={onAdd} className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-[#0A0C10] text-[#FBFAF7] text-sm font-medium hover:bg-[#0A0C10]/90 transition-colors" data-testid="empty-add-chapter">
        <Plus size={14} /> Add chapter
      </button>
    </div>
  );
}
function PageZoom({ page, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-6" onClick={onClose}>
      <div className="relative max-w-4xl max-h-full" onClick={(e) => e.stopPropagation()}>
        <img src={pageUrl(page.image_url)} alt={page.summary} className="rounded-2xl max-h-[88vh] max-w-full object-contain shadow-2xl" />
        <button onClick={onClose} className="absolute top-2 right-2 w-9 h-9 rounded-full bg-white text-[#0A0C10] inline-flex items-center justify-center hover:scale-110 transition-transform"><X size={16} /></button>
        <div className="absolute bottom-3 left-3 right-3 bg-white/95 backdrop-blur rounded-xl p-3">
          <div className="text-[11px] font-mono uppercase tracking-wider text-[#A855F7] font-bold">Page {page.order}</div>
          <div className="text-sm text-[#0A0C10]">{page.summary}</div>
        </div>
      </div>
    </div>
  );
}

// ───────── Add Chapter modal ─────────
function AddChapterModal({ subjectId, onClose, onCreated }) {
  const [mode, setMode] = useState("upload");
  const [title, setTitle] = useState("");
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  // Per-page rotation in degrees (0/90/180/270). Applied client-side via
  // canvas before uploading so the OCR pipeline receives an upright page.
  const [rotations, setRotations] = useState([]);
  const [busy, setBusy] = useState(false);
  const [stream, setStream] = useState(null);
  // Auto-capture loop — when enabled, the modal snaps a page every AUTO_INTERVAL
  // seconds with a 3-2-1 countdown so the teacher just needs to point the camera
  // and turn pages. `countdown` is the visible number over the video (null = idle).
  const [autoOn, setAutoOn] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const [flashing, setFlashing] = useState(false);
  const AUTO_START = 3;    // seconds counted down before each snap
  const AUTO_GAP   = 2;    // pause after a snap before the next countdown starts
  const videoRef = useRef(null);
  const fileInputRef = useRef(null);
  useEffect(() => {
    if (mode === "camera") {
      (async () => {
        try {
          const s = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
          setStream(s);
          if (videoRef.current) { videoRef.current.srcObject = s; videoRef.current.play(); }
        } catch { toast.error("Camera permission denied"); }
      })();
    } else {
      // Leaving camera tab — always stop the auto loop
      setAutoOn(false); setCountdown(null);
    }
    return () => { stream?.getTracks().forEach((t) => t.stop()); };
    /* eslint-disable-next-line */
  }, [mode]);
  const capturePage = () => {
    if (!videoRef.current) return null;
    const v = videoRef.current;
    const canvas = document.createElement("canvas");
    canvas.width = v.videoWidth || 1280; canvas.height = v.videoHeight || 720;
    canvas.getContext("2d").drawImage(v, 0, 0, canvas.width, canvas.height);
    return new Promise((resolve) => {
      canvas.toBlob((b) => {
        if (!b) return resolve(null);
        const f = new File([b], `page-${Date.now()}.png`, { type: "image/png" });
        setFiles((cur) => [...cur, f]);
        setPreviews((cur) => [...cur, URL.createObjectURL(f)]);
        setRotations((cur) => [...cur, 0]);
        // brief white flash so the teacher sees the shutter fire
        setFlashing(true);
        setTimeout(() => setFlashing(false), 220);
        resolve(f);
      }, "image/png");
    });
  };
  // Auto-capture loop — runs while `autoOn` is true and the camera tab is active.
  useEffect(() => {
    if (!autoOn || mode !== "camera") { setCountdown(null); return; }
    let cancelled = false;
    let n = AUTO_START;
    setCountdown(n);
    const tick = async () => {
      if (cancelled) return;
      if (n <= 1) {
        // snap
        setCountdown(0);
        await capturePage();
        if (cancelled) return;
        // pause before the next countdown so the teacher can flip the page
        setCountdown(null);
        setTimeout(() => {
          if (cancelled) return;
          n = AUTO_START;
          setCountdown(n);
          setTimeout(tick, 1000);
        }, AUTO_GAP * 1000);
      } else {
        n -= 1;
        setCountdown(n);
        setTimeout(tick, 1000);
      }
    };
    const timer = setTimeout(tick, 1000);
    return () => { cancelled = true; clearTimeout(timer); };
    /* eslint-disable-next-line */
  }, [autoOn, mode]);
  const onPickFiles = (e) => {
    const list = Array.from(e.target.files || []).filter((f) => f.type.startsWith("image/"));
    if (list.length === 0) return toast.error("Pick image files");
    setFiles((cur) => [...cur, ...list]);
    setPreviews((cur) => [...cur, ...list.map((f) => URL.createObjectURL(f))]);
    setRotations((cur) => [...cur, ...list.map(() => 0)]);
  };
  // Rotate one preview 90° clockwise. The actual pixel rotation is applied
  // just before upload in `submit()` so we don't repeatedly re-encode PNGs.
  const rotatePreview = (i) => {
    setRotations((cur) => cur.map((r, j) => (j === i ? (r + 90) % 360 : r)));
  };
  // Draw a rotated copy of `file` on a canvas and return a fresh File. Called
  // for every page whose rotation is non-zero right before upload.
  const applyRotation = (file, deg) => {
    if (!deg) return Promise.resolve(file);
    return new Promise((resolve) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const swap = deg === 90 || deg === 270;
        canvas.width = swap ? img.height : img.width;
        canvas.height = swap ? img.width : img.height;
        const ctx = canvas.getContext("2d");
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((deg * Math.PI) / 180);
        ctx.drawImage(img, -img.width / 2, -img.height / 2);
        canvas.toBlob((b) => {
          URL.revokeObjectURL(url);
          if (!b) return resolve(file);
          resolve(new File([b], file.name.replace(/(\.[^.]+)?$/, `-r${deg}$1`), { type: "image/png" }));
        }, "image/png");
      };
      img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
      img.src = url;
    });
  };
  const submit = async () => {
    if (files.length === 0) return toast.error("Capture or upload at least one page");
    setBusy(true);
    try {
      const t = (title.trim() || `Chapter ${Date.now().toString().slice(-4)}`);
      const c = await api.post("/chapters", { subject_id: subjectId, title: t });
      const cid = c.data.chapter.id;
      // Apply rotations one file at a time so a corrupt image doesn't cascade.
      const rotated = await Promise.all(files.map((f, i) => applyRotation(f, rotations[i] || 0)));
      const fd = new FormData();
      rotated.forEach((f) => fd.append("files", f));
      await api.post(`/chapters/${cid}/pages`, fd);
      toast.success(`Chapter "${t}" created with ${files.length} page${files.length === 1 ? "" : "s"}`);
      stream?.getTracks().forEach((t) => t.stop());
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl rounded-lg p-6 max-h-[92vh] overflow-y-auto shadow-2xl border themed-border" onClick={(e) => e.stopPropagation()} data-testid="add-chapter-modal">
        <div className="flex items-start justify-between gap-4 mb-5 pb-4 border-b themed-border">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-neutral-500 inline-flex items-center gap-1.5"><Camera size={11} /> Add a chapter</div>
            <h3 className="font-display font-bold text-2xl tracking-tighter mt-1">Snap or upload your chapter</h3>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-[#0A0C10] transition-colors"><X size={18} /></button>
        </div>
        <div className="border themed-border rounded-md p-4 text-sm text-neutral-600 mb-5 bg-black/[0.02]">
          <div className="font-semibold text-[#0A0C10] mb-2 inline-flex items-center gap-1.5 text-[13px]"><Camera size={14} /> How to add a chapter</div>
          <ul className="list-disc list-inside space-y-1 text-[13px] leading-relaxed">
            <li>Give your chapter a short title (optional).</li>
            <li>Snap every page using the webcam or upload images.</li>
            <li>Mobi reads the pages and builds stories, games and a quiz.</li>
          </ul>
        </div>
        <div className="flex gap-1 mb-4 border-b themed-border">
          <ModeBtn label="Camera" icon={Camera} active={mode === "camera"} onClick={() => setMode("camera")} testid="mode-camera" />
          <ModeBtn label="Upload" icon={Upload} active={mode === "upload"} onClick={() => setMode("upload")} testid="mode-upload" />
        </div>
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Chapter title (optional)" data-testid="chapter-title-input" className="w-full px-4 py-2.5 rounded-md bg-white border themed-border outline-none focus:border-[#0A0C10] text-sm mb-4" />
        {mode === "camera" ? (
          <div>
            <div className="relative rounded-md overflow-hidden bg-black aspect-video border themed-border">
              <video ref={videoRef} className="w-full h-full object-cover" muted playsInline />

              {/* Countdown overlay — big number pulses over the video */}
              {countdown !== null && countdown > 0 && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none" data-testid="camera-countdown">
                  <div className="w-28 h-28 rounded-full bg-black/55 backdrop-blur-sm flex items-center justify-center animate-pulse">
                    <span className="font-display font-bold text-6xl text-white tabular-nums">{countdown}</span>
                  </div>
                </div>
              )}
              {countdown === 0 && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <span className="text-white font-display font-bold text-3xl bg-[#10B981]/85 px-4 py-2 rounded-md">Snap!</span>
                </div>
              )}
              {/* Between cycles — reassure the teacher the loop is still running (Ninis #20) */}
              {autoOn && countdown === null && !flashing && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none" data-testid="camera-waiting">
                  <div className="px-3 py-1.5 rounded-full bg-black/60 backdrop-blur-sm text-white text-xs font-mono uppercase tracking-widest inline-flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse" />
                    Flip page · next snap in {AUTO_GAP}s
                  </div>
                </div>
              )}

              {/* Shutter flash */}
              {flashing && <div className="absolute inset-0 bg-white/85 animate-pulse" style={{ animationDuration: "220ms" }} />}

              {/* Auto badge */}
              {autoOn && (
                <div className="absolute top-2 left-2 bg-[#10B981] text-white text-[10px] font-mono font-bold uppercase tracking-wider px-2 py-1 rounded-sm inline-flex items-center gap-1" data-testid="auto-capture-on">
                  <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> Auto · every {AUTO_START + AUTO_GAP}s
                </div>
              )}
              {/* Snap counter — grows with every capture so the teacher knows the loop is working */}
              {(files.length > 0 || autoOn) && (
                <div className="absolute top-2 right-2 bg-black/60 text-white text-[10px] font-mono font-bold px-2 py-1 rounded-sm" data-testid="camera-snap-count">
                  {files.length} page{files.length === 1 ? "" : "s"} captured
                </div>
              )}
            </div>

            <div className="mt-3 grid grid-cols-2 gap-2">
              <button
                onClick={() => setAutoOn((v) => !v)}
                data-testid="auto-capture-toggle"
                className={`inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md text-sm font-medium border transition-colors ${
                  autoOn
                    ? "bg-[#10B981] text-white border-[#10B981] hover:bg-[#059669]"
                    : "bg-white text-[#0A0C10] border-neutral-300 hover:border-[#0A0C10]"
                }`}
              >
                <Sparkles size={14} />
                {autoOn ? "Stop auto-capture" : "Start auto-capture"}
              </button>
              <button
                onClick={capturePage}
                data-testid="capture-page"
                disabled={autoOn}
                className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md bg-[#0A0C10] text-[#FBFAF7] text-sm font-medium hover:bg-[#0A0C10]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Camera size={16} /> Snap page now
              </button>
            </div>
            <p className="text-[11px] text-neutral-500 mt-2 font-mono">
              {autoOn
                ? "Hold the book steady — it will keep snapping every few seconds. Flip a page after each Snap."
                : "Tap Start auto-capture to snap pages automatically with a 3-second countdown between each."}
            </p>
          </div>
        ) : (
          <button onClick={() => fileInputRef.current?.click()} data-testid="upload-pages-btn" className="w-full border-2 border-dashed themed-border rounded-md p-8 text-center hover:border-[#0A0C10] transition-colors">
            <Upload className="mx-auto text-neutral-400" size={26} />
            <div className="text-sm font-semibold mt-3 text-[#0A0C10]">Click to pick images</div>
            <div className="text-xs text-neutral-500 mt-1">PNG / JPG · multiple allowed · up to 25 MB each</div>
            <input ref={fileInputRef} type="file" multiple accept="image/*" hidden onChange={onPickFiles} data-testid="upload-input" />
          </button>
        )}
        {previews.length > 0 && (
          <div className="mt-4">
            <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-neutral-500 mb-2">Pages to upload · {previews.length} · hover for rotate / remove</div>
            <div className="grid grid-cols-4 gap-2">
              {previews.map((src, i) => {
                const deg = rotations[i] || 0;
                return (
                <div key={i} className="relative aspect-[3/4] rounded-md overflow-hidden border themed-border group bg-neutral-100" data-testid={`preview-${i}`}>
                  <img src={src} alt={`Page ${i + 1}`}
                       className="w-full h-full object-cover transition-transform"
                       style={{ transform: `rotate(${deg}deg)` }} />
                  <div className="absolute top-1 left-1 bg-[#0A0C10] text-[#FBFAF7] text-[10px] font-mono font-bold px-1.5 py-0.5 rounded-sm">P{i + 1}</div>
                  <button
                    onClick={() => rotatePreview(i)}
                    data-testid={`preview-rotate-${i}`}
                    title="Rotate 90°"
                    className="absolute bottom-1 left-1 w-6 h-6 rounded-sm bg-[#FFD600] text-[#0A0C10] inline-flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <RotateCw size={12} />
                  </button>
                  <button
                    onClick={() => { setFiles((c) => c.filter((_, j) => j !== i)); setPreviews((c) => c.filter((_, j) => j !== i)); setRotations((c) => c.filter((_, j) => j !== i)); }}
                    data-testid={`preview-remove-${i}`}
                    className="absolute top-1 right-1 w-5 h-5 rounded-sm bg-black/70 text-white inline-flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X size={10} />
                  </button>
                </div>
                );
              })}
            </div>
          </div>
        )}
        <button onClick={submit} disabled={busy || files.length === 0} data-testid="process-continue" className="mt-5 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md bg-[#0A0C10] text-[#FBFAF7] text-sm font-medium hover:bg-[#0A0C10]/90 transition-colors disabled:opacity-50">
          {busy ? <><Loader2 size={16} className="animate-spin" /> Processing…</> : <><Sparkles size={14} /> Process & Continue</>}
        </button>
      </div>
    </div>
  );
}
function ModeBtn({ label, icon: Icon, active, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className={`inline-flex items-center gap-2 px-4 py-2.5 text-[13px] font-medium transition-colors border-b-2 -mb-px ${active ? "border-[#0A0C10] text-[#0A0C10]" : "border-transparent text-neutral-500 hover:text-[#0A0C10]"}`}><Icon size={14} strokeWidth={1.6} /> {label}</button>
  );
}
function pageUrl(url) {
  if (!url) return "";
  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  return `${base}/api${url}`;
}
